import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  ExternalLink, Package, MapPin, Home, RefreshCw, Search, MessageCircle,
  ChevronDown, ChevronRight, Upload, Trash2, Image as ImageIcon, SlidersHorizontal, X, Check,
  Plus, FlaskConical, PhoneCall, CheckCircle2, Ticket, Percent, TrendingUp,
} from "lucide-react";
import PhoneAction from "@/components/PhoneAction";
import StatusFlowModal from "@/components/StatusFlowModal";
import Vouchere from "./Vouchere";
import Whatsapp from "./Whatsapp";
import ApeluriRecenziiSettings from "./ApeluriRecenziiSettings";
import FamiliiRecenzii from "./FamiliiRecenzii";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Culori pe status apel — user cerință Feb 2026. Cheile suplimentare
// (nu_raspunde, refuza_postare) primesc culori neutre acceptabile fiindcă
// user-ul a specificat doar 5. `recenzie_confirmata` e virtual: aplicat
// când call_status = de_acord_cadou_* și există cel puțin o dovadă.
const STATUS_STYLES = {
  in_asteptare:               { label: "În Așteptare Apel",  dot: "bg-amber-400",   pill: "border-amber-500/40 bg-amber-500/10 text-amber-200",       tabActive: "border-amber-400 text-amber-200" },
  nu_a_raspuns:               { label: "Nu a răspuns",       dot: "bg-sky-400",     pill: "border-sky-500/40 bg-sky-500/10 text-sky-100",             tabActive: "border-sky-400 text-sky-100" },
  nr_indisponibil:            { label: "Nr indisponibil",    dot: "bg-rose-400",    pill: "border-rose-500/40 bg-rose-500/10 text-rose-200",          tabActive: "border-rose-400 text-rose-200" },
  nu_raspunde:                { label: "Nu răspunde",        dot: "bg-zinc-400",    pill: "border-zinc-600 bg-zinc-800/60 text-zinc-200",             tabActive: "border-zinc-300 text-zinc-100" },
  refuza_postare:             { label: "Nu postează",        dot: "bg-orange-400",  pill: "border-orange-500/40 bg-orange-500/10 text-orange-200",   tabActive: "border-orange-400 text-orange-200" },
  // User request Feb 2026 — 3 sub-outcomes din „Da":
  //   • Da + mesaj → CADOU DA verde
  //   • Da fără mesaj → CADOU DA albastru
  //   • Nu (căutăm în eMAG) → CADOU NU mov
  de_acord_cadou_da:              { label: "CADOU DA",          dot: "bg-emerald-400", pill: "border-emerald-500/40 bg-emerald-500/10 text-emerald-100", tabActive: "border-emerald-400 text-emerald-100" },
  de_acord_cadou_da_fara_mesaj:   { label: "CADOU DA",          dot: "bg-sky-400",     pill: "border-sky-500/40 bg-sky-500/10 text-sky-100",             tabActive: "border-sky-400 text-sky-100" },
  de_acord_cadou_nu:              { label: "CADOU NU",          dot: "bg-violet-400",  pill: "border-violet-500/40 bg-violet-500/10 text-violet-200",   tabActive: "border-violet-400 text-violet-200" },
  recenzie_confirmata:            { label: "Recenzie confirmată", dot: "bg-emerald-400", pill: "border-emerald-500/40 bg-emerald-500/10 text-emerald-200", tabActive: "border-emerald-400 text-emerald-200" },
};

// Returnează cheia efectivă a stilului: dacă e „A răspuns" + are dovadă,
// afișăm „Recenzie confirmată" (verde) în locul culorii de bază.
function effectiveStatusKey(callStatus, proofCount) {
  const st = callStatus || "in_asteptare";
  const answered = st === "de_acord_cadou_da"
    || st === "de_acord_cadou_da_fara_mesaj"
    || st === "de_acord_cadou_nu";
  if (answered && (proofCount || 0) > 0) return "recenzie_confirmata";
  return st;
}

/**
 * Apeluri Recenzii — listă de clienți din eMAG RO FBE cu comenzi Finalizate
 * mai vechi de N zile, care ar putea fi sunați pentru a lăsa o recenzie.
 * Vezi backend `apeluri_recenzii_module.py` pentru criterii complete.
 *
 * UI refinements Feb 2026: one row per (client, order); filters hidden behind
 * a toggle; product cell shows only image + SKU (name goes into expanded row);
 * proof cell shows thumbnails inline with lightbox popup on click.
 */
export default function ApeluriRecenzii() {
  const [tab, setTab] = useState("comenzi"); // "comenzi" | "vouchere" | "whatsapp" | "pentru_azi"
  const [statusModal, setStatusModal] = useState(null); // {phone, status} or null
  const [olderThan, setOlderThan] = useState(30);
  const [olderThanInput, setOlderThanInput] = useState("30");
  const [data, setData] = useState({ clients: [], count: 0, criteria: null });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [lightbox, setLightbox] = useState(null); // {url, filename} or null
  // Comenzi sub-tab: split by call status per user cerință Feb 2026.
  // Note: `nu_a_raspuns` intentionally absent — that bucket lives in the
  // dedicated „Pentru azi" tab.
  const [statusTab, setStatusTab] = useState("in_asteptare");
  const [sortMode, setSortMode] = useState("date_asc"); // "date_asc" | "sku_asc" | "name_asc"
  const [stats, setStats] = useState(null);
  // Filtre specifice — user cerință Feb 2026.
  const [fName, setFName] = useState("");
  const [fOrderId, setFOrderId] = useState("");
  const [fPhone, setFPhone] = useState("");
  const [fDateFrom, setFDateFrom] = useState("");
  const [fDateTo, setFDateTo] = useState("");
  const [fProductName, setFProductName] = useState("");
  const [fSku, setFSku] = useState("");
  const [fProof, setFProof] = useState(""); // "" = all, "yes" = has proof, "no" = no proof
  const [statusCatalog, setStatusCatalog] = useState([]);
  // Load unused — legacy state kept for backward compat; sub-tabs now
  // replace the manual status filter dropdown.
  void statusCatalog;
  const [expandedRow, setExpandedRow] = useState(null); // composite id "phone::order_id"
  // Selecție bulk pentru tab-ul „Recenzii Confirmate" — Set of "phone::order_id".
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  // Feb 2026 — counts afișate pe fiecare tab (total + unread WA/PentruAzi în roșu).
  const [tabCountsBadge, setTabCountsBadge] = useState({
    vouchere: 0, whatsapp_total: 0, whatsapp_unread: 0, pentru_azi: 0,
  });
  const loadTabCounts = async () => {
    try {
      const r = await axios.get(`${API}/apeluri-recenzii/tab-counts`);
      if (r.data?.ok) setTabCountsBadge({
        vouchere: r.data.vouchere || 0,
        whatsapp_total: r.data.whatsapp_total || 0,
        whatsapp_unread: r.data.whatsapp_unread || 0,
        pentru_azi: r.data.pentru_azi || 0,
      });
    } catch (_e) { /* silent */ }
  };
  useEffect(() => {
    loadTabCounts();
    const id = setInterval(loadTabCounts, 30_000); // refresh la fiecare 30s
    return () => clearInterval(id);
  }, []);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const [r, s] = await Promise.all([
        axios.get(`${API}/apeluri-recenzii`, { params: { older_than_days: olderThan } }),
        axios.get(`${API}/apeluri-recenzii/stats`).catch(() => ({ data: null })),
      ]);
      setData(r.data || { clients: [], count: 0 });
      if (s.data?.ok) setStats(s.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [olderThan]);

  useEffect(() => {
    // Load call-status catalog once — used to populate the status filter.
    (async () => {
      try {
        const r = await axios.get(`${API}/apeluri-recenzii/statuses`);
        setStatusCatalog(r.data?.statuses || []);
      } catch (_e) { /* best-effort */ }
    })();
    // Preload persisted `older_than_days` from Settings so the value the
    // user last saved becomes the default (top input still allows a session
    // override for quick tuning).
    (async () => {
      try {
        const r = await axios.get(`${API}/apeluri-recenzii/settings`);
        const s = r.data?.settings;
        if (s?.older_than_days != null) {
          setOlderThan(s.older_than_days);
          setOlderThanInput(String(s.older_than_days));
        }
      } catch (_e) { /* best-effort */ }
    })();
  }, []);

  const STATUS_TAB_CONFIG = useMemo(() => ([
    { key: "in_asteptare",  label: "În Așteptare Apel", statuses: ["in_asteptare"] },
    { key: "a_raspuns",     label: "A răspuns",          statuses: ["de_acord_cadou_da", "de_acord_cadou_da_fara_mesaj", "de_acord_cadou_nu"] },
    { key: "nr_indisponibil", label: "Nr indisponibil", statuses: ["nr_indisponibil"] },
    { key: "nu_raspunde",   label: "Nu răspunde",       statuses: ["nu_raspunde"] },
    { key: "refuza_postare", label: "Nu postează",      statuses: ["refuza_postare"] },
    // Recenzii Confirmate — user request Feb 2026: filtru pe review_status,
    // NU pe call_status. Un rând poate fi în „refuza_postare" apel dar cu
    // dovadă urcată → tot ajunge aici. Handled în `rows` useMemo mai jos.
    { key: "recenzii_confirmate", label: "Recenzii Confirmate", statuses: [], reviewStatus: "recenzie_confirmata" },
  ]), []);

  // Flatten to one row per (client, order) so column semantics stay 1:1 with
  // the row — user requirement Feb 2026. Client-level filters (name, phone,
  // status, proof) drop the whole client; order-level filters isolate the
  // exact matching orders.
  const allRows = useMemo(() => {
    const nq = fName.trim().toLowerCase();
    const oq = fOrderId.trim();
    const pq = fPhone.trim();
    const pnq = fProductName.trim().toLowerCase();
    const sq = fSku.trim().toLowerCase();
    const pfq = fProof;
    const dfrom = fDateFrom || "";
    const dto = fDateTo || "";
    const out = [];
    for (const c of data.clients || []) {
      if (nq && !c.name.toLowerCase().includes(nq)) continue;
      if (pq && !c.phone.includes(pq)) continue;
      if (pfq === "yes" && (c.proof_count || 0) === 0) continue;
      if (pfq === "no" && (c.proof_count || 0) > 0) continue;
      for (const o of c.orders || []) {
        if (oq && !String(o.order_id || "").includes(oq)) continue;
        if (dfrom && (o.order_date || "") < dfrom) continue;
        if (dto && (o.order_date || "") > dto) continue;
        if (pnq && !(o.products || []).some((p) => (p.name || "").toLowerCase().includes(pnq))) continue;
        if (sq) {
          const skuList = sq.split(",").map((x) => x.trim()).filter(Boolean);
          if (skuList.length > 0 && !(o.products || []).some((p) => skuList.some((sk) => (p.sku || "").toLowerCase().includes(sk)))) continue;
        }
        out.push({ client: c, order: o });
      }
    }
    // Feb 2026 — sortare selectabilă din UI (butoane lângă „Filtre").
    // Default: „date_asc" (cea mai veche sus, cea mai nouă jos).
    const firstSku = (r) => {
      const ps = r.order?.products || [];
      return ps.length ? String(ps[0].sku || "").toLowerCase() : "";
    };
    out.sort((a, b) => {
      if (sortMode === "sku_asc") {
        const sa = firstSku(a), sb = firstSku(b);
        if (sa !== sb) return sa < sb ? -1 : 1;
      } else if (sortMode === "name_asc") {
        const na = String(a.client?.name || "").toLowerCase();
        const nb = String(b.client?.name || "").toLowerCase();
        if (na !== nb) return na < nb ? -1 : 1;
      }
      // Default sau tiebreaker: data crescător apoi order_id.
      const da = a.order?.order_date || "";
      const dbb = b.order?.order_date || "";
      if (da !== dbb) return da < dbb ? -1 : 1;
      return String(a.order?.order_id || "").localeCompare(String(b.order?.order_id || ""));
    });
    return out;
  }, [data, fName, fOrderId, fPhone, fDateFrom, fDateTo, fProductName, fSku, fProof, sortMode]);

  // Per-sub-tab counts (recomputed only when the base filtered rows change).
  const tabCounts = useMemo(() => {
    const counts = {};
    for (const cfg of STATUS_TAB_CONFIG) counts[cfg.key] = 0;
    for (const r of allRows) {
      // Recenzii Confirmate — user cerință Feb 2026: comenzile cu recenzie
      // confirmată se MUTĂ aici, NU se duplică în tab-urile de call_status.
      if (r.order?.review_status === "recenzie_confirmata") {
        counts.recenzii_confirmate = (counts.recenzii_confirmate || 0) + 1;
        continue; // skip counting in call-status tabs
      }
      const st = r.client.call_status || "in_asteptare";
      for (const cfg of STATUS_TAB_CONFIG) {
        if (cfg.statuses.includes(st)) { counts[cfg.key] += 1; break; }
      }
    }
    return counts;
  }, [allRows, STATUS_TAB_CONFIG]);

  const rows = useMemo(() => {
    const cfg = STATUS_TAB_CONFIG.find((t) => t.key === statusTab) || STATUS_TAB_CONFIG[0];
    if (cfg.reviewStatus) {
      return allRows.filter((r) => r.order?.review_status === cfg.reviewStatus);
    }
    // Exclude rândurile cu recenzie confirmată — ele apar DOAR în tab-ul
    // dedicat (mutare, nu duplicare).
    return allRows.filter((r) =>
      cfg.statuses.includes(r.client.call_status || "in_asteptare")
      && r.order?.review_status !== "recenzie_confirmata"
    );
  }, [allRows, statusTab, STATUS_TAB_CONFIG]);

  const clearFilters = () => {
    setFName(""); setFOrderId(""); setFPhone(""); setFDateFrom("");
    setFDateTo(""); setFProductName(""); setFSku(""); setFProof("");
  };
  const hasActiveFilters = Boolean(
    fName || fOrderId || fPhone || fDateFrom || fDateTo || fProductName || fSku || fProof,
  );
  const activeFilterCount = [fName, fOrderId, fPhone, fDateFrom, fDateTo, fProductName, fSku, fProof]
    .filter(Boolean).length;

  useEffect(() => { setPage(1); }, [
    fName, fOrderId, fPhone, fDateFrom, fDateTo, fProductName, fSku, fProof,
    pageSize, olderThan, statusTab,
  ]);

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const paged = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return rows.slice(start, start + pageSize);
  }, [rows, safePage, pageSize]);

  const distinctClientsInRows = useMemo(() => {
    const s = new Set();
    for (const r of rows) s.add(r.client.phone);
    return s.size;
  }, [rows]);

  // Feb 2026 — features exclusive tab-ului „Recenzii Confirmate":
  // (1) coloană Plată la final, (2) checkbox de selecție + bulk actions,
  // (3) buton „Marchează ultimele X ca plătite".
  const isConfirmedTab = statusTab === "recenzii_confirmate";
  const rowKey = (r) => `${r.client.phone}::${r.order.order_id}`;

  // Când se schimbă tab-ul, curăță selecția (relevantă doar pe Recenzii Confirmate).
  useEffect(() => {
    if (!isConfirmedTab && selectedRows.size > 0) setSelectedRows(new Set());
  }, [isConfirmedTab, selectedRows.size]);

  const toggleRowSelect = (key) => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    const visibleKeys = paged.map(rowKey);
    setSelectedRows((prev) => {
      const allSelected = visibleKeys.length > 0 && visibleKeys.every((k) => prev.has(k));
      const next = new Set(prev);
      if (allSelected) {
        visibleKeys.forEach((k) => next.delete(k));
      } else {
        visibleKeys.forEach((k) => next.add(k));
      }
      return next;
    });
  };

  const setPaymentStatusSingle = async (phone, orderId, status) => {
    // Optimistic — patch local data then fire request.
    setData((prev) => ({
      ...prev,
      clients: (prev.clients || []).map((c) => {
        if (c.phone !== phone) return c;
        return {
          ...c,
          orders: (c.orders || []).map((o) =>
            String(o.order_id) === String(orderId) ? { ...o, payment_status: status } : o,
          ),
        };
      }),
    }));
    try {
      await axios.patch(
        `${API}/apeluri-recenzii/${encodeURIComponent(phone)}/orders/${encodeURIComponent(orderId)}/payment`,
        { status },
      );
    } catch (e) {
      toast.error("Actualizare plată eșuată", { description: e.response?.data?.detail || e.message });
      load();
    }
  };

  const bulkSetPayment = async (status) => {
    if (selectedRows.size === 0) return;
    const items = [...selectedRows].map((k) => {
      const [phone, order_id] = k.split("::");
      return { phone, order_id };
    });
    setBulkBusy(true);
    try {
      const r = await axios.post(`${API}/apeluri-recenzii/payments/bulk`, { items, status });
      toast.success(`${r.data?.updated || 0} rânduri marcate: ${status === "platit" ? "Plătit" : "Neplătit"}`);
      setSelectedRows(new Set());
      load();
    } catch (e) {
      toast.error("Bulk plată eșuat", { description: e.response?.data?.detail || e.message });
    } finally { setBulkBusy(false); }
  };

  const markLastPaid = async () => {
    const raw = window.prompt("Câte recenzii recente marchezi ca Plătite?\n(în ordinea afișată aici — cele mai recente sus)", "10");
    if (raw === null) return;
    const count = parseInt(raw, 10);
    if (!count || count <= 0) { toast.error("Număr invalid"); return; }
    // Trimitem lista curentă vizibilă din tab-ul Confirmate (`rows` deja filtrat),
    // sortată cum e afișată. Backend-ul ia primele `count`.
    const items = rows.slice(0, count).map((r) => ({ phone: r.client.phone, order_id: r.order.order_id }));
    if (items.length === 0) { toast.error("Nicio recenzie în listă"); return; }
    if (!window.confirm(`Marchez primele ${items.length} recenzii ca Plătite?`)) return;
    setBulkBusy(true);
    try {
      const r = await axios.post(`${API}/apeluri-recenzii/payments/mark-last-paid`, { count: items.length, items });
      toast.success(`${r.data?.updated || 0} recenzii marcate ca Plătite`);
      load();
    } catch (e) {
      toast.error("Acțiune eșuată", { description: e.response?.data?.detail || e.message });
    } finally { setBulkBusy(false); }
  };

  return (
    <div className="space-y-6 p-4" data-testid="apeluri-recenzii-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-zinc-100">Apeluri Recenzii</h1>
        </div>
      </div>

      {/* Feb 2026 — Toate KPI-urile pe un singur rând orizontal:
          [Recenzii Obținute] [Nr apeluri] [Vouchere] [Rata Conversie] [Rata Reală]
          Fiecare din primele 2 = 2x2 mini-grid (Azi/Ieri sus, LC/LT jos).
          Casete înguste — doar cât să încapă „LUNA CURENTĂ". */}
      {(() => {
        const va = stats?.vouchers_available ?? 0;
        const tc = stats?.total_confirmed ?? 0;
        const ta = stats?.total_answered ?? 0;
        const tn = stats?.total_non_asteptare ?? 0;
        const rate = (num, den) => (den > 0 ? Math.round((num / den) * 1000) / 10 : null);
        const rConv = rate(tc, ta);
        const rReal = rate(tc, tn);
        // 2x2 mini-grid pentru cele 2 blocuri stânga.
        const buildQuad = (obj) => [
          { key: "azi",       label: "Azi",          value: obj.today },
          { key: "ieri",      label: "Ieri",         value: obj.yesterday },
          { key: "luna",      label: "Luna curentă", value: obj.this_month },
          { key: "luna-trec", label: "Luna trecută", value: obj.last_month },
        ];
        const recenziiObtinute = buildQuad({
          today:      stats?.confirmed_today,
          yesterday:  stats?.confirmed_yesterday,
          this_month: stats?.confirmed_this_month,
          last_month: stats?.confirmed_last_month,
        });
        const nrApeluri = buildQuad({
          today:      stats?.answered_today,
          yesterday:  stats?.answered_yesterday,
          this_month: stats?.answered_this_month,
          last_month: stats?.answered_last_month,
        });
        return (
          <div className="flex flex-wrap gap-2" data-testid="kpi-row">
            {/* 1. Recenzii Obținute */}
            <div className="flex-1 min-w-[260px] rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-2" data-testid="stats-card-confirmed">
              <div className="mb-1.5 flex items-center gap-1.5">
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-300" />
                <h2 className="text-xs font-semibold text-zinc-100">Recenzii Obținute</h2>
              </div>
              <div className="grid grid-cols-2 gap-1">
                {recenziiObtinute.map((s) => (
                  <div
                    key={s.key}
                    data-testid={`stat-confirmed-${s.key}`}
                    className="rounded border border-emerald-500/20 bg-emerald-500/5 px-1.5 py-0.5"
                  >
                    <div className="text-[9px] uppercase tracking-wider text-emerald-300/70 truncate">{s.label}</div>
                    <div className="text-sm font-semibold leading-tight tabular-nums text-emerald-100">
                      {s.value ?? "—"}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 2. Nr apeluri */}
            <div className="flex-1 min-w-[260px] rounded-lg border border-zinc-800 bg-zinc-950 p-2" data-testid="stats-card">
              <div className="mb-1.5 flex items-center gap-1.5">
                <PhoneCall className="h-3.5 w-3.5 text-emerald-300" />
                <h2 className="text-xs font-semibold text-zinc-100">Nr apeluri</h2>
              </div>
              <div className="grid grid-cols-2 gap-1">
                {nrApeluri.map((s) => (
                  <div
                    key={s.key}
                    data-testid={`stat-answered-${s.key}`}
                    className="rounded border border-zinc-800 bg-zinc-900/40 px-1.5 py-0.5"
                  >
                    <div className="text-[9px] uppercase tracking-wider text-zinc-500 truncate">{s.label}</div>
                    <div className="text-sm font-semibold leading-tight tabular-nums text-zinc-100">
                      {s.value ?? "—"}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 3-5. Vouchere / Rată Conversie / Rată Reală */}
            <div className="flex-1 min-w-[140px] rounded-lg border border-amber-500/30 bg-amber-500/5 p-2" data-testid="kpi-vouchere-disponibile">
              <div className="mb-0.5 flex items-center gap-1.5">
                <Ticket className="h-4 w-4 text-amber-300" />
                <span className="text-[10px] font-medium uppercase tracking-wider text-zinc-300">Vouchere Disponibile</span>
              </div>
              <div className="text-lg font-semibold leading-tight tabular-nums text-amber-100">{va}</div>
              <div className="text-[9px] text-zinc-500">din tab-ul Vouchere</div>
            </div>
            <div className="flex-1 min-w-[140px] rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-2" data-testid="kpi-rata-conversie">
              <div className="mb-0.5 flex items-center gap-1.5">
                <Percent className="h-4 w-4 text-emerald-300" />
                <span className="text-[10px] font-medium uppercase tracking-wider text-zinc-300">Rata Conversie</span>
              </div>
              <div className="text-lg font-semibold leading-tight tabular-nums text-emerald-100">
                {rConv == null ? "—" : `${rConv}%`}
              </div>
              <div className="text-[9px] text-zinc-500">{tc} recenzii / {ta} au răspuns</div>
            </div>
            <div className="flex-1 min-w-[140px] rounded-lg border border-sky-500/30 bg-sky-500/5 p-2" data-testid="kpi-rata-reala">
              <div className="mb-0.5 flex items-center gap-1.5">
                <TrendingUp className="h-4 w-4 text-sky-300" />
                <span className="text-[10px] font-medium uppercase tracking-wider text-zinc-300">Rata Reală</span>
              </div>
              <div className="text-lg font-semibold leading-tight tabular-nums text-sky-100">
                {rReal == null ? "—" : `${rReal}%`}
              </div>
              <div className="text-[9px] text-zinc-500">{tc} recenzii / {tn} contactați</div>
            </div>
          </div>
        );
      })()}

      {/* Tab-uri */}
      <div className="flex gap-1 border-b border-zinc-800">
        {[
          { key: "comenzi", label: "Comenzi" },
          { key: "familii", label: "Familii" },
          { key: "vouchere", label: "Vouchere" },
          { key: "whatsapp", label: "WhatsApp", unread: tabCountsBadge.whatsapp_unread },
          { key: "pentru_azi", label: "Pentru azi", unread: tabCountsBadge.pentru_azi },
          { key: "setari", label: "Setări" },
        ].map((t) => (
          <button
            key={t.key}
            data-testid={`tab-${t.key.replace("_", "-")}`}
            onClick={() => setTab(t.key)}
            className={`-mb-px inline-flex items-center gap-1.5 border-b-2 px-4 py-2 text-sm font-medium transition ${
              tab === t.key ? "border-orange-500 text-orange-200"
                            : "border-transparent text-zinc-400 hover:text-zinc-200"
            }`}
          >
            <span>{t.label}</span>
            {typeof t.unread === "number" && t.unread > 0 && (
              <span
                data-testid={`tab-unread-${t.key.replace("_", "-")}`}
                className="rounded-full bg-rose-500/20 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-rose-300"
                title={t.key === "whatsapp" ? "Contacte cu mesaje necitite" : "Contacte de sunat azi"}
              >
                ({t.unread})
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === "vouchere" && <Vouchere />}
      {tab === "familii" && (
        <FamiliiRecenzii
          onNavigateToComenzi={(skus, familyName) => {
            // Feb 2026: click pe „Vezi comenzi în așteptare" din tab-ul Familii
            // → comută la tab-ul Comenzi, sub-tab „În Așteptare Apel" și
            // pune primul SKU în filtrul de SKU (existent). User poate
            // rafina apoi manual dacă familia are mai multe SKU-uri.
            setTab("comenzi");
            setStatusTab("in_asteptare");
            clearFilters();
            if (skus && skus.length > 0) {
              // Multi-SKU: unim SKU-urile cu virgulă → filtrul de tabel
              // împarte pe virgulă și afișează orice comandă care conține
              // vreunul din SKU-urile familiei.
              setFSku(skus.join(","));
              if (skus.length > 1) {
                toast.info(`Familia „${familyName}" — filtrat pe ${skus.length} SKU-uri: ${skus.join(", ")}`);
              }
            }
          }}
        />
      )}
      {tab === "whatsapp" && <Whatsapp />}
      {tab === "pentru_azi" && <PentruAziTab onStatusClick={(c, o) => {
        const firstProduct = ((o || {}).products || [])[0] || {};
        setStatusModal({
          phone: c.phone,
          status: c.call_status,
          order_id: (o || {}).order_id,
          product_image_url: firstProduct.image_url || "",
          product_name: firstProduct.name || "",
        });
      }} />}
      {tab === "setari" && (
        <ApeluriRecenziiSettings
          onSettingsChanged={(s) => {
            if (s?.older_than_days != null) {
              setOlderThan(s.older_than_days);
              setOlderThanInput(String(s.older_than_days));
            }
          }}
        />
      )}

      {statusModal && (
        <StatusFlowModal
          phone={statusModal.phone}
          currentStatus={statusModal.status}
          orderId={statusModal.order_id}
          productImageUrl={statusModal.product_image_url}
          productName={statusModal.product_name}
          onClose={() => setStatusModal(null)}
          onSaved={load}
        />
      )}

      {lightbox && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={() => setLightbox(null)}
          data-testid="proof-lightbox"
        >
          <button
            type="button"
            className="absolute right-4 top-4 rounded-full bg-zinc-900/80 p-2 text-zinc-200 hover:bg-zinc-800"
            onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
            data-testid="proof-lightbox-close"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="max-h-[92vh] max-w-[92vw]" onClick={(e) => e.stopPropagation()}>
            <img src={lightbox.url} alt={lightbox.filename || ""} className="max-h-[92vh] max-w-[92vw] rounded-lg object-contain" />
            {lightbox.filename && (
              <div className="mt-2 text-center text-xs text-zinc-300">{lightbox.filename}</div>
            )}
          </div>
        </div>
      )}

      {tab === "comenzi" && <>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              data-testid="toggle-filters-btn"
              onClick={() => setShowFilters((v) => !v)}
              className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm transition ${
                showFilters || hasActiveFilters
                  ? "border-orange-500/50 bg-orange-500/10 text-orange-100"
                  : "border-zinc-800 bg-zinc-900/60 text-zinc-200 hover:border-zinc-700"
              }`}
            >
              <SlidersHorizontal className="h-3.5 w-3.5" />
              Filtre
              {activeFilterCount > 0 && (
                <span className="ml-1 rounded-full bg-orange-500/30 px-1.5 py-0 text-[10px] font-semibold text-orange-100">
                  {activeFilterCount}
                </span>
              )}
            </button>
            {hasActiveFilters && (
              <button
                type="button"
                data-testid="clear-filters-inline"
                onClick={clearFilters}
                className="text-[11px] text-zinc-400 underline underline-offset-2 hover:text-zinc-200"
              >
                Șterge filtrele
              </button>
            )}
            {/* Feb 2026 — 2 butoane de sortare instant (lângă Filtre). */}
            <div className="ml-1 inline-flex items-center gap-1" data-testid="sort-buttons">
              <button
                type="button"
                onClick={() => setSortMode(sortMode === "sku_asc" ? "date_asc" : "sku_asc")}
                data-testid="sort-sku-asc"
                title="Sortează după SKU (crescător)"
                className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-medium transition ${
                  sortMode === "sku_asc"
                    ? "border-orange-500/60 bg-orange-500/15 text-orange-100"
                    : "border-zinc-800 bg-zinc-900/60 text-zinc-300 hover:border-zinc-700"
                }`}
              >
                <Package className="h-3 w-3" /> SKU ↑
              </button>
              <button
                type="button"
                onClick={() => setSortMode(sortMode === "name_asc" ? "date_asc" : "name_asc")}
                data-testid="sort-name-asc"
                title="Sortează după nume client (A-Z)"
                className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-medium transition ${
                  sortMode === "name_asc"
                    ? "border-orange-500/60 bg-orange-500/15 text-orange-100"
                    : "border-zinc-800 bg-zinc-900/60 text-zinc-300 hover:border-zinc-700"
                }`}
              >
                Nume A→Z
              </button>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              data-testid="refresh-btn"
              onClick={load}
              disabled={loading}
              className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-800 bg-zinc-900/60 px-3 py-1.5 text-sm text-zinc-200 hover:border-zinc-700 disabled:opacity-50"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
              Reîncarcă
            </button>
            <button
              data-testid="add-test-order-btn"
              onClick={() => setShowTestModal(true)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-fuchsia-500/40 bg-fuchsia-500/10 px-3 py-1.5 text-sm text-fuchsia-100 hover:bg-fuchsia-500/20"
              title="Adaugă o comandă test pentru a rula tot flow-ul (status, dovezi, WhatsApp)"
            >
              <FlaskConical className="h-3.5 w-3.5" />
              Comandă test
            </button>
          </div>
        </div>

        {showTestModal && (
          <TestOrderModal
            onClose={() => setShowTestModal(false)}
            onSaved={load}
          />
        )}

        {showFilters && (
          <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-3" data-testid="filters-panel">
            <div className="mb-2 flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs text-zinc-500">
                <Search className="h-3.5 w-3.5" />
                <span>Filtrează rezultatele</span>
                {hasActiveFilters && (
                  <span className="rounded-full bg-orange-500/20 px-2 py-0.5 text-[10px] text-orange-200">
                    {rows.length} rânduri · {distinctClientsInRows} clienți
                  </span>
                )}
              </div>
              {hasActiveFilters && (
                <button
                  data-testid="clear-filters"
                  onClick={clearFilters}
                  className="text-[11px] text-zinc-400 underline underline-offset-2 hover:text-zinc-200"
                >
                  Șterge toate filtrele
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2 md:grid-cols-3 xl:grid-cols-7">
              <input data-testid="filter-name" placeholder="Nume client" value={fName} onChange={(e) => setFName(e.target.value)} className="rounded-md border border-zinc-800 bg-zinc-900/60 px-2 py-1.5 text-xs text-zinc-100 placeholder:text-zinc-600 focus:border-zinc-700 focus:outline-none" />
              <input data-testid="filter-order-id" placeholder="Nr comandă" value={fOrderId} onChange={(e) => setFOrderId(e.target.value)} className="rounded-md border border-zinc-800 bg-zinc-900/60 px-2 py-1.5 text-xs text-zinc-100 placeholder:text-zinc-600 focus:border-zinc-700 focus:outline-none" />
              <input data-testid="filter-phone" placeholder="Nr telefon" value={fPhone} onChange={(e) => setFPhone(e.target.value)} className="rounded-md border border-zinc-800 bg-zinc-900/60 px-2 py-1.5 text-xs text-zinc-100 placeholder:text-zinc-600 focus:border-zinc-700 focus:outline-none" />
              <div className="flex items-center gap-1">
                <input data-testid="filter-date-from" type="date" value={fDateFrom} onChange={(e) => setFDateFrom(e.target.value)} title="Dată comandă (de la)" className="flex-1 rounded-md border border-zinc-800 bg-zinc-900/60 px-2 py-1.5 text-xs text-zinc-100 focus:border-zinc-700 focus:outline-none" />
                <span className="text-zinc-600">→</span>
                <input data-testid="filter-date-to" type="date" value={fDateTo} onChange={(e) => setFDateTo(e.target.value)} title="Dată comandă (până la)" className="flex-1 rounded-md border border-zinc-800 bg-zinc-900/60 px-2 py-1.5 text-xs text-zinc-100 focus:border-zinc-700 focus:outline-none" />
              </div>
              <input data-testid="filter-product-name" placeholder="Nume produs" value={fProductName} onChange={(e) => setFProductName(e.target.value)} className="rounded-md border border-zinc-800 bg-zinc-900/60 px-2 py-1.5 text-xs text-zinc-100 placeholder:text-zinc-600 focus:border-zinc-700 focus:outline-none" />
              <input data-testid="filter-sku" placeholder="SKU (ex: BRAG-146)" value={fSku} onChange={(e) => setFSku(e.target.value)} className="rounded-md border border-zinc-800 bg-zinc-900/60 px-2 py-1.5 text-xs text-zinc-100 placeholder:text-zinc-600 focus:border-zinc-700 focus:outline-none" />
              <select data-testid="filter-proof" value={fProof} onChange={(e) => setFProof(e.target.value)} title="Filtru dovadă recenzie" className="rounded-md border border-zinc-800 bg-zinc-900/60 px-2 py-1.5 text-xs text-zinc-100 focus:border-zinc-700 focus:outline-none">
                <option value="">Dovadă: toate</option>
                <option value="yes">Cu dovadă</option>
                <option value="no">Fără dovadă</option>
              </select>
            </div>
          </div>
        )}

        {/* Sub-tab-uri pe status apel — user cerință Feb 2026. */}
        <div className="flex flex-wrap gap-1 border-b border-zinc-800">
          {STATUS_TAB_CONFIG.map((t) => {
            const active = statusTab === t.key;
            const count = tabCounts[t.key] || 0;
            // Pentru sub-tab activ folosim culoarea din STATUS_STYLES ca să
            // se lege vizual de badge-urile din tabel.
            const styleKey = t.statuses[0]; // reprezentantul bucket-ului
            const style = STATUS_STYLES[styleKey] || STATUS_STYLES.in_asteptare;
            return (
              <button
                key={t.key}
                type="button"
                data-testid={`status-tab-${t.key}`}
                onClick={() => setStatusTab(t.key)}
                className={`-mb-px inline-flex items-center gap-1.5 border-b-2 px-3 py-1.5 text-xs font-medium transition ${
                  active ? style.tabActive : "border-transparent text-zinc-400 hover:text-zinc-200"
                }`}
              >
                <span className={`h-1.5 w-1.5 rounded-full ${style.dot} ${active ? "opacity-100" : "opacity-60"}`} />
                {t.label}
                <span className={`rounded-full px-1.5 py-0 text-[10px] ${
                  active ? style.pill : "bg-zinc-800 text-zinc-400"
                }`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {error && (
          <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-200">
            {error}
          </div>
        )}

        {/* Feb 2026 — Bulk bar + „Marchează ultimele X" doar pe tab-ul Recenzii Confirmate */}
        {isConfirmedTab && (
          <div
            className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-3 py-2 text-sm"
            data-testid="confirmed-bulk-bar"
          >
            <div className="flex items-center gap-2 text-emerald-100">
              <span data-testid="confirmed-selected-count">
                {selectedRows.size > 0
                  ? `${selectedRows.size} selectat${selectedRows.size === 1 ? "" : "e"}`
                  : "Bulk plată: bifează rânduri sau folosește „Ultimele X”."}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => bulkSetPayment("platit")}
                disabled={selectedRows.size === 0 || bulkBusy}
                data-testid="bulk-mark-paid"
                className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-100 hover:bg-emerald-500/20 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Marchează Plătit
              </button>
              <button
                type="button"
                onClick={() => bulkSetPayment("neplatit")}
                disabled={selectedRows.size === 0 || bulkBusy}
                data-testid="bulk-mark-unpaid"
                className="rounded-md border border-rose-500/40 bg-rose-500/10 px-2.5 py-1 text-xs font-medium text-rose-100 hover:bg-rose-500/20 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Marchează Neplătit
              </button>
              <span className="mx-1 h-4 w-px bg-emerald-500/20" />
              <button
                type="button"
                onClick={markLastPaid}
                disabled={bulkBusy || rows.length === 0}
                data-testid="mark-last-paid"
                className="rounded-md border border-amber-500/40 bg-amber-500/10 px-2.5 py-1 text-xs font-medium text-amber-100 hover:bg-amber-500/20 disabled:opacity-40"
              >
                Marchează ultimele X ca Plătite
              </button>
            </div>
          </div>
        )}

        <div className="overflow-hidden rounded-xl border border-zinc-800 bg-zinc-950">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-zinc-900/70 text-[11px] uppercase text-zinc-500">
                <tr>
                  <th className="w-8 px-2 py-2" />
                  {isConfirmedTab && (
                    <th className="w-8 px-2 py-2">
                      <input
                        type="checkbox"
                        checked={paged.length > 0 && paged.every((r) => selectedRows.has(rowKey(r)))}
                        onChange={toggleSelectAllVisible}
                        title="Selectează toate vizibile"
                        data-testid="select-all-visible"
                        className="h-3.5 w-3.5 cursor-pointer accent-emerald-500"
                      />
                    </th>
                  )}
                  <th className="px-3 py-2 text-left font-medium">Nume client</th>
                  <th className="px-3 py-2 text-left font-medium">Telefon</th>
                  <th className="px-3 py-2 text-left font-medium">Nr comandă</th>
                  <th className="px-3 py-2 text-left font-medium">Produs</th>
                  <th className="px-3 py-2 text-center font-medium">Mesaj Recenzie</th>
                  <th className="px-3 py-2 text-center font-medium">R1</th>
                  <th className="px-3 py-2 text-center font-medium">R2</th>
                  <th className="px-3 py-2 text-left font-medium">Status apel</th>
                  <th className="px-3 py-2 text-left font-medium">Status Recenzie</th>
                  <th className="px-3 py-2 text-left font-medium">Dovadă recenzie</th>
                  {isConfirmedTab && (
                    <th className="px-3 py-2 text-left font-medium">Plată</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {loading && rows.length === 0 && (
                  <tr><td colSpan={isConfirmedTab ? 13 : 11} className="px-3 py-8 text-center text-zinc-500">Se încarcă…</td></tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr><td colSpan={isConfirmedTab ? 13 : 11} className="px-3 py-8 text-center text-zinc-500">Nicio comandă în intervalul selectat.</td></tr>
                )}
                {paged.map(({ client, order }) => {
                  const rowKeyLocal = `${client.phone}::${order.order_composite_id || order.order_id}`;
                  const selKey = `${client.phone}::${order.order_id}`;
                  const isOpen = expandedRow === rowKeyLocal;
                  const wa_href = `/whatsapp-recenzii?phone=${encodeURIComponent(client.phone)}&order=${order.order_id}`;
                  return (
                    <FlatRow
                      key={rowKeyLocal}
                      client={client}
                      order={order}
                      isOpen={isOpen}
                      onToggle={() => setExpandedRow(isOpen ? null : rowKeyLocal)}
                      onStatusClick={() => {
                        const firstProduct = (order.products || [])[0] || {};
                        setStatusModal({
                          phone: client.phone,
                          status: client.call_status || "in_asteptare",
                          order_id: order.order_id,
                          product_image_url: firstProduct.image_url || "",
                          product_name: firstProduct.name || "",
                        });
                      }}
                      onCallLogged={load}
                      onReviewStatusChanged={load}
                      onLightbox={setLightbox}
                      wa_href={wa_href}
                      showPaymentCol={isConfirmedTab}
                      showSelectCol={isConfirmedTab}
                      isSelected={selectedRows.has(selKey)}
                      onToggleSelect={() => toggleRowSelect(selKey)}
                      onPaymentChange={(status) => setPaymentStatusSingle(client.phone, order.order_id, status)}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-zinc-900 bg-zinc-950 px-3 py-2 text-xs text-zinc-400">
            <div className="flex items-center gap-2">
              <span>Pe pagină:</span>
              {[25, 50, 100].map((n) => (
                <button
                  key={n}
                  type="button"
                  data-testid={`page-size-${n}`}
                  onClick={() => setPageSize(n)}
                  className={`rounded px-2 py-0.5 ${pageSize === n
                    ? "bg-orange-500/90 text-white"
                    : "border border-zinc-800 bg-zinc-900/60 text-zinc-300 hover:border-zinc-700"}`}
                >
                  {n}
                </button>
              ))}
              <span className="ml-3 text-zinc-500">
                Afișare {rows.length === 0 ? 0 : (safePage - 1) * pageSize + 1}–{Math.min(safePage * pageSize, rows.length)} din {rows.length}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button type="button" data-testid="page-first" disabled={safePage <= 1} onClick={() => setPage(1)} className="rounded border border-zinc-800 bg-zinc-900/60 px-2 py-0.5 disabled:opacity-40">« Prima</button>
              <button type="button" data-testid="page-prev" disabled={safePage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="rounded border border-zinc-800 bg-zinc-900/60 px-2 py-0.5 disabled:opacity-40">‹ Anterior</button>
              <span className="px-2 text-zinc-300" data-testid="page-indicator">
                Pagina <strong>{safePage}</strong> / {totalPages}
              </span>
              <button type="button" data-testid="page-next" disabled={safePage >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="rounded border border-zinc-800 bg-zinc-900/60 px-2 py-0.5 disabled:opacity-40">Următor ›</button>
              <button type="button" data-testid="page-last" disabled={safePage >= totalPages} onClick={() => setPage(totalPages)} className="rounded border border-zinc-800 bg-zinc-900/60 px-2 py-0.5 disabled:opacity-40">Ultima »</button>
            </div>
          </div>
        </div>
      </>}
    </div>
  );
}

function FlatRow({ client, order, isOpen, onToggle, onStatusClick, onCallLogged, onReviewStatusChanged, onLightbox, wa_href, showPaymentCol, showSelectCol, isSelected, onToggleSelect, onPaymentChange }) {
  const DIcon = order.delivery?.mode === "locker" ? MapPin : Home;
  const [r1, setR1] = useState(Boolean(order.reminder_1));
  const [r2, setR2] = useState(Boolean(order.reminder_2));
  const [msgSent, setMsgSent] = useState(Boolean(order.message_sent));
  const [remindersOff, setRemindersOff] = useState(Boolean(order.reminders_off));
  const [savingReminder, setSavingReminder] = useState(false);

  useEffect(() => {
    setR1(Boolean(order.reminder_1));
    setR2(Boolean(order.reminder_2));
    setMsgSent(Boolean(order.message_sent));
    setRemindersOff(Boolean(order.reminders_off));
  }, [order.reminder_1, order.reminder_2, order.message_sent, order.reminders_off]);

  // Feb 2026 (user request) — reguli disable per status apel + status recenzie.
  //  • CADOU DA           → MR chekable, R1 chekable, R2 blocat (auto scheduler)
  //  • CADOU DA fără mesaj→ MR X roșu, R1 chekable, R2 blocat
  //  • CADOU NU           → MR X roșu, R1+R2 blocate (nimic nu se trimite)
  //  • In așteptare / Nu răspunde / Nu răspuns / Nr indisponibil / Refuză
  //                       → MR X roșu, R1+R2 blocate
  //  • review = recenzie_confirmată → orice buton care NU e deja bifat rămâne
  //    dezactivat (nu mai trimitem nimic după ce clientul a lăsat recenzie).
  const cs = client.call_status || "in_asteptare";
  const isCadouDa = cs === "de_acord_cadou_da";
  const isCadouDaFaraMesaj = cs === "de_acord_cadou_da_fara_mesaj";
  const isConfirmed = order.review_status === "recenzie_confirmata";
  // MR: blocat cu X roșu în TOATE statusurile EXCEPT „CADOU DA".
  // Când review = confirmată și MR nu a fost trimis, îl blocăm și el.
  const msgBlocked = !isCadouDa || (isConfirmed && !msgSent);
  // R1: chekable doar în „CADOU DA" sau „CADOU DA fără mesaj".
  // Când review = confirmată și R1 nu a fost trimis, îl blocăm.
  const r1Disabled = !(isCadouDa || isCadouDaFaraMesaj) || (isConfirmed && !r1);
  // R2: mereu blocat manual — scheduler-ul trimite auto 72h după R1.
  // Când review = confirmată, se blochează total (inclusiv scheduler-ul).
  const r2Disabled = true;

  const toggleReminder = async (which, currentValue, setter) => {
    const next = !currentValue;
    setter(next); // optimistic
    setSavingReminder(true);
    try {
      const r = await axios.patch(
        `${API}/apeluri-recenzii/${encodeURIComponent(client.phone)}/orders/${encodeURIComponent(order.order_id)}/reminders`,
        { reminder: which, value: next },
      );
      // Feb 2026 — surface WhatsApp send result: succes = toast verde, esec
      // = toast rosu cu motivul (config incomplet, template inexistent, etc.)
      // ca user-ul sa stie de ce nu ajunge mesajul.
      const wa = r.data?.wa_send;
      if (wa && next && (which === 1 || which === 2 || which === "message_sent")) {
        if (wa.ok) {
          const label = which === "message_sent" ? "Mesaj Recenzie" : `Reminder ${which}`;
          toast.success(`${label} trimis pe WhatsApp`, { description: `Template: ${wa.template}` });
        } else {
          const reasonMap = {
            wa_config_incomplete: "WhatsApp CRM neconfigurat (waba_id / access_token lipsă)",
            wa_config_disabled: "WhatsApp CRM dezactivat din setări",
            template_not_configured: "Template neconfigurat în setări",
          };
          const label = which === "message_sent" ? "Mesajul Recenzie" : `Reminder ${which}`;
          toast.error(`${label} nu s-a trimis pe WhatsApp`, {
            description: reasonMap[wa.reason] || wa.reason,
          });
        }
      }
    } catch (_e) {
      setter(currentValue); // revert
      toast.error("Eșec la salvare");
    } finally {
      setSavingReminder(false);
    }
  };
  return (
    <>
      <tr
        className="cursor-pointer border-t border-zinc-900 align-top hover:bg-zinc-900/40"
        data-testid={`row-${client.phone}-${order.order_id}`}
        onClick={(e) => {
          // Nu declanșa expand când click-ul e pe orice element interactiv
          // (linkuri, butoane, checkbox-uri, dropdown, input). Restul din
          // rând este zonă de expand — user request Feb 2026.
          if (e.target.closest("a, button, input, label, select, textarea, [role='button']")) return;
          onToggle();
        }}
      >
        <td className="px-2 py-3">
          <button
            type="button"
            onClick={onToggle}
            className="flex h-5 w-5 items-center justify-center rounded text-zinc-500 hover:bg-zinc-800 hover:text-zinc-200"
            title={isOpen ? "Ascunde detalii" : "Arată detalii"}
            data-testid={`row-toggle-${order.order_id}`}
          >
            {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          </button>
        </td>
        {showSelectCol && (
          <td className="px-2 py-3">
            <input
              type="checkbox"
              checked={Boolean(isSelected)}
              onChange={onToggleSelect}
              onClick={(e) => e.stopPropagation()}
              data-testid={`row-select-${client.phone}-${order.order_id}`}
              className="h-3.5 w-3.5 cursor-pointer accent-emerald-500"
              title="Selectează pentru acțiune bulk"
            />
          </td>
        )}
        <td className="px-3 py-3">
          <div className="flex items-center gap-1.5">
            <span className="font-medium text-zinc-100">{client.name}</span>
            {client.is_test && (
              <span
                className="inline-flex items-center gap-0.5 rounded-full border border-fuchsia-500/40 bg-fuchsia-500/10 px-1.5 py-0 text-[9px] font-semibold uppercase tracking-wider text-fuchsia-200"
                title="Comandă adăugată manual pentru test"
                data-testid={`test-badge-${order.order_id}`}
              >
                <FlaskConical className="h-2.5 w-2.5" /> test
              </span>
            )}
          </div>
        </td>
        <td className="px-3 py-3">
          <PhoneAction
            phone={client.phone}
            onCall={async () => {
              try {
                await axios.post(`${API}/apeluri-recenzii/${encodeURIComponent(client.phone)}/call-log`);
                onCallLogged?.();
              } catch (_e) { /* silent */ }
              // User request Feb 2026: după log-ul apelului, deschide auto
              // dropdown-ul „Status apel" ca operatorul să seteze imediat
              // rezultatul (a răspuns / nu răspunde / etc.) fără un click extra.
              onStatusClick?.();
            }}
          />
          {(client.call_logs || []).length > 0 && (
            <span
              className="ml-1 inline-flex items-center gap-1 rounded bg-emerald-500/10 px-1.5 py-0.5 text-[10px] text-emerald-300"
              title={(client.call_logs || []).map((l) => l.at).join("\n")}
              data-testid={`call-count-${client.phone}`}
            >
              {client.call_logs.length}× apel
            </span>
          )}
        </td>
        <td className="px-3 py-3">
          {order.is_test ? (
            <div className="flex items-center gap-1.5">
              <span
                className="font-mono text-sm text-fuchsia-200"
                data-testid={`test-order-id-${order.order_id}`}
              >
                {order.order_id}
              </span>
              <button
                type="button"
                onClick={async () => {
                  if (!window.confirm(`Ștergi comanda test ${order.order_id}?`)) return;
                  try {
                    await axios.delete(`${API}/apeluri-recenzii/test-orders/${encodeURIComponent(order.order_id)}`);
                    onCallLogged?.(); // reuse the reload prop
                  } catch (e) {
                    alert(`Ștergere eșuată: ${e.response?.data?.detail || e.message}`);
                  }
                }}
                title="Șterge comandă test"
                data-testid={`test-order-delete-${order.order_id}`}
                className="rounded p-1 text-rose-400 hover:bg-rose-500/10 hover:text-rose-200"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          ) : (
            <Link
              to={`/orders?search=${order.order_id}`}
              className="font-mono text-sm text-orange-300 hover:text-orange-100 hover:underline"
              title="Deschide în pagina Comenzi"
              data-testid={`order-link-${order.order_id}`}
            >
              #{order.order_id}
            </Link>
          )}
        </td>
        <td className="px-3 py-3">
          <div className="flex flex-wrap gap-1.5">
            {(order.products || []).map((p, idx) => (
              <a
                key={`${order.order_id}-${idx}-${p.sku}`}
                href={p.emag_url || "#"}
                target={p.emag_url ? "_blank" : undefined}
                rel="noreferrer noopener"
                className={`group flex items-center gap-1.5 rounded-md border border-zinc-800 bg-zinc-900/40 p-1 pr-2 text-xs transition hover:border-zinc-600 ${!p.emag_url ? "pointer-events-none opacity-60" : ""}`}
                title={p.emag_url ? `${p.name} — deschide pagina eMAG` : p.name}
                data-testid={`product-${order.order_id}-${p.sku}`}
              >
                {p.image_url ? (
                  <img
                    src={p.image_url}
                    alt={p.sku}
                    className="h-9 w-9 flex-shrink-0 rounded object-cover"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                ) : (
                  <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded bg-zinc-900 text-zinc-600">
                    <Package className="h-4 w-4" />
                  </div>
                )}
                <span className="font-mono text-[11px] font-semibold text-zinc-100">
                  {p.sku}
                  {p.quantity > 1 && <span className="ml-1 text-zinc-500">×{p.quantity}</span>}
                </span>
                {p.emag_url && (
                  <ExternalLink className="h-3 w-3 flex-shrink-0 text-zinc-600 group-hover:text-zinc-400" />
                )}
              </a>
            ))}
          </div>
        </td>
        <td className="px-3 py-3 text-center">
          {msgBlocked ? (
            // MR blocat: X roșu (statuses ≠ „CADOU DA", sau review confirmată
            // și MR încă netrimis).  Când MR e deja trimis + review confirmată,
            // afișăm bifa (nu resetăm history-ul).
            msgSent ? (
              <span
                className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-emerald-500/50 bg-emerald-500/10 text-emerald-300"
                title="Mesaj recenzie trimis (blocat de status)"
                data-testid={`msg-sent-locked-${order.order_id}`}
              >
                <Check className="h-3 w-3" />
              </span>
            ) : (
              <span
                className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-rose-500/50 bg-rose-500/10 text-rose-300"
                title={
                  isConfirmed
                    ? "Recenzie confirmată — nu mai trimitem Mesaj Recenzie"
                    : "Status curent nu permite trimiterea Mesajului Recenzie"
                }
                data-testid={`msg-sent-blocked-${order.order_id}`}
              >
                <X className="h-3 w-3" />
              </span>
            )
          ) : (
            <input
              type="checkbox"
              checked={msgSent}
              disabled={savingReminder}
              onChange={() => toggleReminder("message_sent", msgSent, setMsgSent)}
              className="h-4 w-4 cursor-pointer accent-emerald-500"
              title="Mesaj recenzie trimis"
              data-testid={`msg-sent-${order.order_id}`}
            />
          )}
        </td>
        <td className="px-3 py-3 text-center">
          <div className="flex flex-col items-center gap-1">
            <input
              type="checkbox"
              checked={r1}
              disabled={savingReminder || remindersOff || r1Disabled}
              onChange={() => toggleReminder(1, r1, setR1)}
              className="h-4 w-4 cursor-pointer accent-orange-500 disabled:opacity-40"
              title={
                r1Disabled
                  ? (isConfirmed
                    ? "Recenzie confirmată — nu mai trimitem R1"
                    : "R1 se poate trimite doar în Cadou DA sau Cadou DA fără mesaj")
                  : (remindersOff ? "Remindere dezactivate pentru acest client" : "Reminder 1")
              }
              data-testid={`reminder-1-${order.order_id}`}
            />
            {/* Label „Reminders" (jumătate stânga) — se citește împreună cu „Off ☐" din R2 */}
            <span className="mt-0.5 self-end pr-1 text-[9px] uppercase tracking-wider text-zinc-500">
              Reminders
            </span>
          </div>
        </td>
        <td className="px-3 py-3 text-center">
          <div className="flex flex-col items-center gap-1">
            <input
              type="checkbox"
              checked={r2}
              disabled={true}
              onChange={() => toggleReminder(2, r2, setR2)}
              className="h-4 w-4 cursor-not-allowed accent-orange-500 opacity-40"
              title="R2 se trimite automat 72h după R1 (nu se activează manual)"
              data-testid={`reminder-2-${order.order_id}`}
            />
            {/* „Off ☐" — checkbox real pentru toggle-ul reminders_off */}
            <label
              className="mt-0.5 flex items-center gap-1 self-start pl-1 text-[9px] uppercase tracking-wider text-zinc-500 hover:text-zinc-300"
              title="Bifează ca să oprești toate reminderele automate pentru acest client"
            >
              Off
              <input
                type="checkbox"
                checked={remindersOff}
                disabled={savingReminder}
                onChange={() => toggleReminder("off", remindersOff, setRemindersOff)}
                data-testid={`reminders-off-${order.order_id}`}
                className="h-3 w-3 cursor-pointer accent-rose-500"
              />
            </label>
          </div>
        </td>
        <td className="px-3 py-3">
          {(() => {
            const styleKey = effectiveStatusKey(client.call_status, client.proof_count);
            const style = STATUS_STYLES[styleKey] || STATUS_STYLES.in_asteptare;
            const label = styleKey === "recenzie_confirmata"
              ? "Recenzie confirmată"
              : (client.call_status_label || style.label);
            return (
              <button
                type="button"
                onClick={onStatusClick}
                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium hover:brightness-110 ${style.pill}`}
                data-testid={`call-status-${client.phone}`}
                title="Click ca să schimbi statusul"
              >
                <span className={`h-1.5 w-1.5 rounded-full ${style.dot}`} />
                {label}
              </button>
            );
          })()}
        </td>
        <td className="px-3 py-3">
          <ReviewStatusCell
            phone={client.phone}
            orderId={order.order_id}
            status={order.review_status || "fara_recenzie"}
            override={order.review_status_override}
            onChanged={onReviewStatusChanged}
          />
        </td>
        <td className="px-3 py-3">
          <ProofCell phone={client.phone} initialCount={client.proof_count || 0} onLightbox={onLightbox} onProofsChanged={onReviewStatusChanged} />
        </td>
        {showPaymentCol && (
          <td className="px-3 py-3">
            {(() => {
              const paid = (order.payment_status || "neplatit") === "platit";
              return (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onPaymentChange?.(paid ? "neplatit" : "platit"); }}
                  data-testid={`payment-${client.phone}-${order.order_id}`}
                  title={paid ? "Marchează ca Neplătit" : "Marchează ca Plătit"}
                  className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium hover:brightness-110 ${
                    paid
                      ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
                      : "border-rose-500/40 bg-rose-500/10 text-rose-200"
                  }`}
                >
                  <span className={`h-1.5 w-1.5 rounded-full ${paid ? "bg-emerald-400" : "bg-rose-400"}`} />
                  {paid ? "Plătit" : "Neplătit"}
                </button>
              );
            })()}
          </td>
        )}
      </tr>
      {isOpen && (
        <tr className="border-t border-zinc-900 bg-zinc-900/30" data-testid={`row-details-${order.order_id}`}>
          <td colSpan={showPaymentCol ? 14 : (showSelectCol ? 13 : 12)} className="px-4 py-3">
            <div className="grid gap-3 md:grid-cols-3">
              <div className="rounded-md border border-zinc-800 bg-zinc-950/60 px-2 py-1.5 text-[11px]">
                <div className="mb-0.5 text-[10px] uppercase tracking-wider text-zinc-500">Data comandă</div>
                <div className="text-zinc-200">{order.order_date || "—"}</div>
                {client.latest_order_date && client.latest_order_date !== order.order_date && (
                  <div className="mt-1 text-[10px] text-zinc-500">
                    Ultima comandă client: {client.latest_order_date}
                  </div>
                )}
                <div className="mt-1 text-[10px] text-zinc-500">
                  Total comenzi client: <span className="text-zinc-300">{client.orders_count}</span>
                </div>
              </div>
              <div className="rounded-md border border-zinc-800 bg-zinc-950/60 px-2 py-1.5 text-[11px]" data-testid={`delivery-${order.order_id}`}>
                <div className="mb-0.5 flex items-center gap-1 text-[10px] uppercase tracking-wider text-zinc-500">
                  <DIcon className="h-3 w-3" /> Livrare
                </div>
                <div className="text-zinc-300">{order.delivery?.label || "—"}</div>
              </div>
              <div className="rounded-md border border-zinc-800 bg-zinc-950/60 px-2 py-1.5 text-[11px]">
                <div className="mb-0.5 text-[10px] uppercase tracking-wider text-zinc-500">Produse (denumire)</div>
                <ul className="space-y-0.5 text-zinc-300">
                  {(order.products || []).map((p, idx) => (
                    <li key={`${order.order_id}-name-${idx}`} className="leading-snug">
                      <span className="font-mono text-[10px] text-zinc-500">{p.sku}</span>{" "}
                      {p.name}
                      {p.quantity > 1 && <span className="ml-1 text-zinc-500">×{p.quantity}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            {/* 2026-02: WhatsApp + Voucher Oferit — vizibile DOAR când
                rândul e expandat (click pe comandă). Erau înainte inline
                sub SKU-uri, user a cerut să apară doar la deschidere. */}
            <div className="mt-2 flex flex-wrap items-center gap-2 rounded-md border border-zinc-800 bg-zinc-950/60 px-2 py-1.5">
              <Link
                to={wa_href}
                className="inline-flex items-center gap-1 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-2 py-1 text-[11px] font-medium text-emerald-200 hover:bg-emerald-500/20"
                title="Deschide conversația WhatsApp cu clientul"
                data-testid={`whatsapp-${order.order_id}`}
                onClick={(e) => e.stopPropagation()}
              >
                <MessageCircle className="h-3 w-3" /> WhatsApp
              </Link>
              {order.voucher_offered ? (
                <span
                  className="inline-flex items-center gap-1 rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1 font-mono text-[11px] font-semibold text-amber-100"
                  title="Voucher oferit clientului"
                  data-testid={`voucher-offered-${order.order_id}`}
                >
                  <Ticket className="h-3 w-3" /> Voucher: {order.voucher_offered}
                </span>
              ) : (
                <span
                  className="inline-flex items-center gap-1 rounded-md border border-zinc-800 bg-zinc-900/40 px-2 py-1 text-[11px] font-medium text-zinc-500"
                  title="Nu s-a oferit voucher pentru această comandă"
                  data-testid={`voucher-offered-${order.order_id}`}
                >
                  <Ticket className="h-3 w-3" /> fără voucher
                </span>
              )}
            </div>
            {(client.call_logs || []).length > 0 && (
              <div className="mt-2 rounded-md border border-emerald-500/20 bg-emerald-500/5 px-2 py-1.5 text-[11px]" data-testid={`call-logs-${order.order_id}`}>
                <div className="mb-1 text-[10px] uppercase tracking-wider text-emerald-300">
                  Istoric apeluri ({client.call_logs.length})
                </div>
                <ol className="space-y-0.5 text-emerald-100">
                  {client.call_logs.map((l, i) => (
                    <li key={i} className="font-mono text-[11px]">
                      <span className="mr-1 text-emerald-400">#{i + 1}</span>
                      Data apel {i + 1}: {l.at}
                      {l.by && <span className="ml-2 text-emerald-500/70">· {l.by}</span>}
                    </li>
                  ))}
                </ol>
              </div>
            )}
            {client.call_status === "refuza_postare" && (
              <div
                className="mt-2 rounded-md border border-rose-500/20 bg-rose-500/5 px-2 py-1.5 text-[11px]"
                data-testid={`refuse-reason-${order.order_id}`}
              >
                <div className="mb-1 text-[10px] uppercase tracking-wider text-rose-300">
                  Motiv refuz postare
                </div>
                <div className="whitespace-pre-wrap text-rose-100">
                  {client.call_notes?.trim() || <span className="italic text-rose-300/60">— fără motiv —</span>}
                </div>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function PentruAziTab({ onStatusClick }) {
  const [data, setData] = useState({ clients: [], count: 0, today: "" });
  const [loading, setLoading] = useState(false);
  const reload = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/apeluri-recenzii/pentru-azi`);
      setData(r.data || { clients: [], count: 0 });
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const toggleReminder = async (phone, orderId, which, next) => {
    // Optimistic: mutate local state, then persist.
    setData((prev) => ({
      ...prev,
      clients: prev.clients.map((c) => {
        if (c.phone !== phone) return c;
        return {
          ...c,
          orders: c.orders.map((o) => o.order_id === orderId
            ? { ...o, [`reminder_${which}`]: next }
            : o),
        };
      }),
    }));
    try {
      await axios.patch(
        `${API}/apeluri-recenzii/${encodeURIComponent(phone)}/orders/${encodeURIComponent(orderId)}/reminders`,
        { reminder: which, value: next },
      );
    } catch (_e) {
      reload(); // revert from server
    }
  };

  return (
    <div className="space-y-3" data-testid="pentru-azi-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-zinc-400">
          Clienți reprogramați pentru azi (<strong>{data.today}</strong>) —
          status „Nu a răspuns" cu data reprogramării ≤ azi.
        </p>
        <button onClick={reload} disabled={loading} className="rounded-md border border-zinc-800 bg-zinc-900/60 px-2 py-1 text-xs text-zinc-200 hover:border-zinc-700">
          {loading ? "..." : "Reîncarcă"}
        </button>
      </div>
      <div className="overflow-hidden rounded-xl border border-zinc-800 bg-zinc-950">
        <table className="min-w-full text-sm">
          <thead className="bg-zinc-900/70 text-[11px] uppercase text-zinc-500">
            <tr>
              <th className="px-3 py-2 text-left">Client</th>
              <th className="px-3 py-2 text-left">Telefon</th>
              <th className="px-3 py-2 text-left">Comandă</th>
              <th className="px-3 py-2 text-center">R1</th>
              <th className="px-3 py-2 text-center">R2</th>
              <th className="px-3 py-2 text-left">Reprogramat pt</th>
              <th className="px-3 py-2 text-left">Acțiune</th>
            </tr>
          </thead>
          <tbody>
            {data.clients.length === 0 && !loading && (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-zinc-500">
                Niciun client de sunat azi.
              </td></tr>
            )}
            {data.clients.map((c) => {
              const o = c.orders[0] || {};
              return (
              <tr key={c.phone} className="border-t border-zinc-900" data-testid={`pentru-azi-row-${c.phone}`}>
                <td className="px-3 py-3 text-zinc-100">{c.name}</td>
                <td className="px-3 py-3">
                  <PhoneAction
                    phone={c.phone}
                      onCall={async () => {
                        try { await axios.post(`${API}/apeluri-recenzii/${encodeURIComponent(c.phone)}/call-log`); reload(); } catch (_e) {}
                        // Feb 2026: după apel deschide direct dropdown-ul de status.
                        onStatusClick?.(c, o);
                      }}
                  />
                </td>
                <td className="px-3 py-3">
                  <Link to={`/orders?search=${o.order_id}`} className="font-mono text-orange-300 hover:text-orange-100">
                    #{o.order_id}
                  </Link>
                  <div className="mt-0.5 text-[10px] text-zinc-500">{o.order_date}</div>
                </td>
                <td className="px-3 py-3 text-center">
                  <input
                    type="checkbox"
                    checked={Boolean(o.reminder_1)}
                    onChange={() => toggleReminder(c.phone, o.order_id, 1, !o.reminder_1)}
                    className="h-4 w-4 cursor-pointer accent-orange-500"
                    title="Reminder 1"
                    data-testid={`pentru-azi-reminder-1-${o.order_id}`}
                  />
                </td>
                <td className="px-3 py-3 text-center">
                  <input
                    type="checkbox"
                    checked={Boolean(o.reminder_2)}
                    onChange={() => toggleReminder(c.phone, o.order_id, 2, !o.reminder_2)}
                    className="h-4 w-4 cursor-pointer accent-orange-500"
                    title="Reminder 2"
                    data-testid={`pentru-azi-reminder-2-${o.order_id}`}
                  />
                </td>
                <td className="px-3 py-3 font-mono text-xs text-amber-200">{c.next_action_date}</td>
                <td className="px-3 py-3">
                  <button
                    onClick={() => onStatusClick(c, o)}
                    data-testid={`pentru-azi-status-${c.phone}`}
                    className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-1 text-xs text-amber-100 hover:bg-amber-500/20"
                  >
                    Schimbă status
                  </button>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * ProofCell — Feb 2026 update: eagerly loads proof metadata on mount when the
 * client has at least one proof so thumbnails appear directly in the table.
 * Clicking a thumbnail opens a full-size lightbox handled by the parent page.
 */
function ProofCell({ phone, initialCount, onLightbox, onProofsChanged }) {
  const [proofs, setProofs] = useState(null);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const loadProofs = async () => {
    try {
      const r = await axios.get(`${API}/apeluri-recenzii/${encodeURIComponent(phone)}/proofs`);
      setProofs(r.data?.proofs || []);
    } catch (_e) {
      setProofs([]);
    }
  };

  useEffect(() => {
    if ((initialCount || 0) > 0 && proofs === null) {
      loadProofs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCount, phone]);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const f of files) {
        const form = new FormData();
        form.append("file", f);
        await axios.post(`${API}/apeluri-recenzii/${encodeURIComponent(phone)}/proofs`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      await loadProofs();
      // Feb 2026: reîncarcă lista părinte → review_status se recalculează
      // (fara_recenzie → recenzie_confirmata) și rândul migrează în tab-ul
      // „Recenzii Confirmate" imediat.
      onProofsChanged?.();
    } catch (err) {
      alert(`Upload eșuat: ${err.response?.data?.detail || err.message}`);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const deleteProof = async (proofId) => {
    if (!window.confirm("Ștergi această dovadă?")) return;
    try {
      await axios.delete(`${API}/apeluri-recenzii/proofs/${proofId}`);
      await loadProofs();
      onProofsChanged?.();
    } catch (err) {
      alert(`Ștergere eșuată: ${err.response?.data?.detail || err.message}`);
    }
  };

  const list = proofs || [];

  return (
    <div className="space-y-1.5" data-testid={`proof-cell-${phone}`}>
      {list.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {list.map((p) => {
            const url = `${API}/apeluri-recenzii/proofs/${p.proof_id}/download`;
            return (
              <div key={p.proof_id} className="group relative">
                <button
                  type="button"
                  onClick={() => onLightbox?.({ url, filename: p.filename })}
                  title={`${p.filename} · ${new Date(p.uploaded_at).toLocaleDateString("ro-RO")}`}
                  data-testid={`proof-${p.proof_id}`}
                  className="block"
                >
                  <img
                    src={url}
                    alt={p.filename}
                    className="h-14 w-14 rounded border border-zinc-700 object-cover transition hover:border-zinc-500"
                  />
                </button>
                <button
                  type="button"
                  onClick={() => deleteProof(p.proof_id)}
                  title="Șterge"
                  data-testid={`proof-delete-${p.proof_id}`}
                  className="absolute -right-1 -top-1 hidden h-4 w-4 items-center justify-center rounded-full bg-rose-500 text-white group-hover:flex"
                >
                  <Trash2 className="h-2.5 w-2.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}
      <div className="flex items-center gap-1.5">
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="image/*"
          className="hidden"
          onChange={onFiles}
          data-testid={`proof-input-${phone}`}
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid={`proof-upload-${phone}`}
          className="inline-flex items-center gap-1 rounded-md border border-sky-500/40 bg-sky-500/10 px-2 py-1 text-[11px] text-sky-100 hover:bg-sky-500/20 disabled:opacity-50"
        >
          <Upload className="h-3 w-3" />
          {uploading ? "Se încarcă…" : list.length > 0 ? "Adaugă" : "Încarcă dovadă"}
        </button>
        {list.length === 0 && (initialCount || 0) === 0 && null}
      </div>
    </div>
  );
}


/**
 * TestOrderModal — Feb 2026: adaugă o comandă manuală (fără eMAG) ca
 * să poți parcurge tot flow-ul CRM (status apel, R1/R2, dovezi,
 * WhatsApp link) fără a depinde de o comandă reală.  order_id se
 * generează în forma `TEST-<timestamp_ms>` în backend.
 */
function TestOrderModal({ onClose, onSaved }) {
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [orderId, setOrderId] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // Product picker
  const [productQuery, setProductQuery] = useState("");
  const [productResults, setProductResults] = useState([]);
  const [productPicked, setProductPicked] = useState(null); // full doc {name, part_number, part_number_key, image, url}
  const [searching, setSearching] = useState(false);
  useEffect(() => {
    if (productPicked) return; // don't search once picked
    const q = productQuery.trim();
    if (q.length < 2) { setProductResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await axios.get(`${API}/products`, { params: { search: q } });
        const items = Array.isArray(r.data) ? r.data : (r.data?.items || r.data?.products || []);
        setProductResults(items.slice(0, 10));
      } catch { setProductResults([]); }
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [productQuery, productPicked]);

  const pickProduct = (p) => {
    setProductPicked(p);
    setProductQuery(p.name || p.part_number || "");
    setProductResults([]);
  };
  const clearProduct = () => { setProductPicked(null); setProductQuery(""); };

  const submit = async (e) => {
    e?.preventDefault?.();
    setError(null);
    const p = phone.trim();
    const n = name.trim();
    const oid = orderId.trim();
    if (!p) return setError("Numărul de telefon e obligatoriu.");
    if (!n) return setError("Numele e obligatoriu.");
    setSaving(true);
    try {
      const body = { phone: p, name: n };
      if (oid) body.order_id = oid;
      if (productPicked) {
        const img = productPicked.image_url
          || productPicked.main_image_url
          || (Array.isArray(productPicked.images) && productPicked.images[0]?.url)
          || null;
        const pnk = productPicked.part_number_key || null;
        body.product = {
          sku: productPicked.part_number || productPicked.sku || null,
          name: productPicked.name || "",
          image_url: img,
          part_number_key: pnk,
          emag_url: pnk ? `https://www.emag.ro/-/pd/${pnk}/` : (productPicked.url || null),
        };
      }
      await axios.post(`${API}/apeluri-recenzii/test-orders`, body);
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
      data-testid="test-order-modal"
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-xl border border-fuchsia-500/30 bg-zinc-950 p-4 shadow-2xl"
      >
        <div className="mb-3 flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-zinc-100">
            <FlaskConical className="h-4 w-4 text-fuchsia-300" />
            Adaugă comandă test
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-zinc-500 hover:bg-zinc-900 hover:text-zinc-200"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="mb-3 text-[11px] text-zinc-500">
          Comanda va apărea în lista principală cu badge <span className="text-fuchsia-200">TEST</span> și
          urmează tot flow-ul (status apel, R1/R2, dovezi, WhatsApp). Poți șterge oricând.
        </p>
        <div className="space-y-2">
          <label className="block">
            <span className="mb-0.5 block text-[11px] text-zinc-400">Nume client</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Ion Popescu"
              data-testid="test-order-name"
              className="w-full rounded-md border border-zinc-800 bg-zinc-900/60 px-2 py-1.5 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-zinc-700 focus:outline-none"
              autoFocus
            />
          </label>
          <label className="block">
            <span className="mb-0.5 block text-[11px] text-zinc-400">Telefon</span>
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Ex: 0700000001"
              data-testid="test-order-phone"
              inputMode="tel"
              className="w-full rounded-md border border-zinc-800 bg-zinc-900/60 px-2 py-1.5 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-zinc-700 focus:outline-none"
            />
          </label>
          <label className="block">
            <span className="mb-0.5 block text-[11px] text-zinc-400">
              Nr comandă <span className="text-zinc-600">(opțional — verifică link-ul din WhatsApp)</span>
            </span>
            <input
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              placeholder="Ex: 123456789 sau TEST-abc — lasă gol pt auto"
              data-testid="test-order-id"
              className="w-full rounded-md border border-zinc-800 bg-zinc-900/60 px-2 py-1.5 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-zinc-700 focus:outline-none"
            />
          </label>
          <div>
            <span className="mb-0.5 block text-[11px] text-zinc-400">
              Produs <span className="text-zinc-600">(opțional — caută în catalog)</span>
            </span>
            {productPicked ? (
              <div className="flex items-center gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/5 px-2 py-1.5" data-testid="test-order-product-picked">
                {(() => {
                  const img = productPicked.image_url || productPicked.main_image_url
                    || (Array.isArray(productPicked.images) && productPicked.images[0]?.url);
                  return img ? (
                    <img src={img} alt="" className="h-10 w-10 shrink-0 rounded border border-zinc-800 object-cover" />
                  ) : (
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded border border-zinc-800 bg-zinc-900 text-[9px] text-zinc-500">fără poză</div>
                  );
                })()}
                <div className="min-w-0 flex-1">
                  <div className="truncate text-xs text-zinc-100">{productPicked.name}</div>
                  <div className="truncate font-mono text-[10px] text-zinc-500">
                    {productPicked.part_number || "—"} {productPicked.part_number_key ? `· PNK ${productPicked.part_number_key}` : ""}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={clearProduct}
                  data-testid="test-order-product-clear"
                  className="rounded p-1 text-zinc-500 hover:bg-zinc-800 hover:text-zinc-200"
                  title="Schimbă produsul"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <div className="relative">
                <input
                  value={productQuery}
                  onChange={(e) => setProductQuery(e.target.value)}
                  placeholder="Caută după nume sau SKU (min. 2 caractere)"
                  data-testid="test-order-product-search"
                  className="w-full rounded-md border border-zinc-800 bg-zinc-900/60 px-2 py-1.5 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-zinc-700 focus:outline-none"
                />
                {productResults.length > 0 && (
                  <div className="mt-1 max-h-60 overflow-y-auto rounded-md border border-zinc-800 bg-zinc-950 shadow-lg" data-testid="test-order-product-results">
                    {productResults.map((p) => {
                      const img = p.image_url || p.main_image_url || (Array.isArray(p.images) && p.images[0]?.url);
                      return (
                        <button
                          key={p.id || p.part_number}
                          type="button"
                          onClick={() => pickProduct(p)}
                          data-testid={`test-order-product-option-${p.part_number || p.id}`}
                          className="flex w-full items-center gap-2 border-b border-zinc-900 px-2 py-1.5 text-left hover:bg-zinc-900"
                        >
                          {img ? (
                            <img src={img} alt="" className="h-8 w-8 shrink-0 rounded border border-zinc-800 object-cover" />
                          ) : (
                            <div className="h-8 w-8 shrink-0 rounded border border-zinc-800 bg-zinc-900" />
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-xs text-zinc-100">{p.name}</div>
                            <div className="truncate font-mono text-[10px] text-zinc-500">
                              {p.part_number || "—"} {p.part_number_key ? `· PNK ${p.part_number_key}` : ""}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
                {searching && (
                  <div className="mt-1 text-[10px] text-zinc-500">Caut…</div>
                )}
              </div>
            )}
          </div>
        </div>
        {error && (
          <div className="mt-2 rounded-md border border-rose-500/40 bg-rose-500/10 px-2 py-1.5 text-[11px] text-rose-200">
            {error}
          </div>
        )}
        <div className="mt-3 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-1.5 text-xs text-zinc-300 hover:border-zinc-700"
          >
            Anulează
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="test-order-submit"
            className="inline-flex items-center gap-1 rounded-md border border-fuchsia-500/50 bg-fuchsia-500/90 px-3 py-1.5 text-xs font-medium text-white hover:bg-fuchsia-400 disabled:opacity-50"
          >
            <Plus className="h-3 w-3" />
            {saving ? "Se adaugă…" : "Adaugă"}
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * ReviewStatusCell — pill click-able cu Status Recenzie per comandă.
 * Fără Recenzie (roșu) = default fără dovadă
 * Recenzie Confirmată (verde) = auto când există dovadă, sau override manual
 * Click pe pill → dropdown cu 3 opțiuni: Fără Recenzie / Recenzie Confirmată / Auto (șterge override).
 */
function ReviewStatusCell({ phone, orderId, status, override, onChanged }) {
  const [open, setOpen] = useState(false);
  const [local, setLocal] = useState(status);
  const [localOverride, setLocalOverride] = useState(override);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setLocal(status); setLocalOverride(override); }, [status, override]);

  const setValue = async (val) => {
    setSaving(true);
    if (val === "") {
      setLocalOverride(null);
    } else {
      setLocal(val);
      setLocalOverride(val);
    }
    try {
      const r = await axios.patch(
        `${API}/apeluri-recenzii/${encodeURIComponent(phone)}/orders/${encodeURIComponent(orderId)}/reminders`,
        { reminder: "review_status", value: val },
      );
      setLocalOverride(r.data?.review_status_override || null);
      if (val === "") toast.success("Override șters — status auto");
      else toast.success(`Status Recenzie: ${val === "recenzie_confirmata" ? "Recenzie Confirmată" : "Fără Recenzie"}`);
      // Fire parent reload — asigură recalculul filtrelor de sub-tab
      // (mutare între „În Așteptare" / „Recenzii Confirmate") + counter-e.
      onChanged?.();
    } catch (e) {
      setLocal(status);
      setLocalOverride(override);
      toast.error("Nu am putut actualiza", { description: e.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
      setOpen(false);
    }
  };

  const isConfirmed = local === "recenzie_confirmata";
  const pillClass = isConfirmed
    ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-100"
    : "border-rose-500/40 bg-rose-500/10 text-rose-100";
  const dotClass = isConfirmed ? "bg-emerald-400" : "bg-rose-400";
  const label = isConfirmed ? "Recenzie Confirmată" : "Fără Recenzie";

  return (
    <div className="relative inline-block" data-testid={`review-status-wrap-${orderId}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={saving}
        data-testid={`review-status-pill-${orderId}`}
        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium hover:brightness-110 disabled:opacity-50 ${pillClass}`}
        title="Click ca să schimbi manual Status Recenzie"
      >
        <span className={`h-1.5 w-1.5 rounded-full ${dotClass}`} />
        {label}
        {localOverride && <span className="ml-0.5 text-[9px] opacity-70">(manual)</span>}
      </button>
      {open && (
        <div
          className="absolute right-0 z-10 mt-1 w-52 rounded-md border border-zinc-800 bg-zinc-950 shadow-xl"
          onMouseLeave={() => setOpen(false)}
          data-testid={`review-status-menu-${orderId}`}
        >
          <button
            type="button"
            onClick={() => setValue("fara_recenzie")}
            className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-[11px] text-zinc-100 hover:bg-rose-500/10"
            data-testid={`review-status-set-fara-${orderId}`}
          >
            <span className="h-1.5 w-1.5 rounded-full bg-rose-400" />
            Fără Recenzie
          </button>
          <button
            type="button"
            onClick={() => setValue("recenzie_confirmata")}
            className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-[11px] text-zinc-100 hover:bg-emerald-500/10"
            data-testid={`review-status-set-confirmata-${orderId}`}
          >
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
            Recenzie Confirmată
          </button>
          <button
            type="button"
            onClick={() => setValue("")}
            className="flex w-full items-center gap-2 border-t border-zinc-800 px-3 py-1.5 text-left text-[10px] text-zinc-400 hover:bg-zinc-900"
            data-testid={`review-status-clear-${orderId}`}
          >
            <RefreshCw className="h-3 w-3" /> Auto (bazat pe dovadă)
          </button>
        </div>
      )}
    </div>
  );
}

